import api from '@dashboard/services/axiosApi'
import type { Offer } from '@dashboard/types/config'
import { funnelDefinitionEach } from '@dashboard/modules/flow/services/funnelDefinitions'
import { useFlowStore } from '@dashboard/modules/flow/stores/flowStore'
import { ROUTES } from '@dashboard/router/routeNames'
import { useConfigStore } from '@dashboard/stores/configStore'

type IntegrationActionFeature =
  | boolean
  | {
      unstructured?: boolean
      arbitrary?: boolean
    }
  | null
  | undefined

export const integrationActionIsStructured = (feature: IntegrationActionFeature) => {
  if (!feature) {
    return false
  }
  if (feature === true) {
    return true
  }

  return !(feature.unstructured || feature.arbitrary)
}

export const isForm = (offer: Offer) => {
  if (!offer) {
    return false
  }
  return offer.triggeredAction?.type !== 'link'
}

export const offerLink = (offer: Offer) => {
  const isCta = !isForm(offer)
  return {
    name: isCta ? ROUTES.OFFER : ROUTES.FORM,
    params: {
      offerId: offer.id
    }
  }
}

export const offerUsedBy = (offer: Offer) => {
  const flowStore = useFlowStore()
  return flowStore.flows.filter((flow) => {
    let found = false
    funnelDefinitionEach(flow.funnelDefinition, (node) => {
      if (node.type === 'offer' && node.offerId === offer.id) {
        found = true
      }
    })
    return found
  })
}

export const offerIsInUse = (offer: Offer) => offerUsedBy(offer).length > 0

export const webhookActionUrlLooksValid = (url?: string | null) => {
  if (!url?.trim()) {
    return false
  }

  try {
    const parsedUrl = new URL(url)
    return parsedUrl.protocol === 'https:' && !!parsedUrl.host
  } catch {
    return false
  }
}

export const offerRequiresNativeIntegration = (offer: Offer) => {
  const actionType = offer.triggeredAction?.type
  return !!actionType && actionType !== 'link' && actionType !== 'webhook'
}

export const offerCanBeSubmitted = (offer: Offer) => {
  const action = offer.triggeredAction

  if (action?.type === 'link') {
    return true
  }
  if (action?.type === 'webhook') {
    return webhookActionUrlLooksValid(action.settings.url)
  }
  if (action?.type === 'form') {
    return action.settings.formIds?.length > 0
  }
  if (action?.type === 'list') {
    return action.settings.listIds?.length > 0
  }
  if (action?.type === 'tag') {
    return action.settings.tagIds?.length > 0
  }
  if (action?.type === 'event') {
    return action.settings.eventIds?.length > 0
  }
  if (action?.type === 'automation') {
    return action.settings.automationIds?.length > 0
  }
  // Contact action doesn't require any IDs - it just creates/updates the contact
  if (action?.type === 'contact') {
    return true
  }
  return false
}

export const classifyOfferPublishability = ({
  offers,
  hasIntegration,
  offerFlows,
  widgetsForFlowId
}: {
  offers: Offer[]
  hasIntegration: boolean
  offerFlows: (offer: Offer) => { id: string }[]
  widgetsForFlowId: (flowId: string) => unknown[]
}) => {
  const missingIntegration = hasIntegration
    ? []
    : offers.filter(
        (offer) =>
          offerRequiresNativeIntegration(offer) &&
          offerFlows(offer).some((flow) => widgetsForFlowId(flow.id).length > 0)
      )

  const invalidConfiguration = offers.filter((offer) => {
    const usedBy = offerFlows(offer)
    if (usedBy.length === 0) {
      return false
    }
    if (offerCanBeSubmitted(offer)) {
      return false
    }

    return hasIntegration || !offerRequiresNativeIntegration(offer)
  })

  return {
    missingIntegration,
    invalidConfiguration
  }
}
type CoreOfferStatsData = {
  offersData?: {
    offerBreakdowns?: {
      offerId?: string
      visitorsExposed?: number
      visitorsConverted?: number
      timeSeries?: { visitorsConverted: number }[]
    }[]
  }
}

export function offerStats(offer: Offer, data: CoreOfferStatsData | null | undefined) {
  const row = data?.offersData?.offerBreakdowns?.find((item) => item.offerId === offer.id)
  if (!row) {
    return null
  }
  const visitorsExposed = row.visitorsExposed ?? 0
  const visitorsConverted = row.visitorsConverted ?? 0
  return {
    visitorsExposed,
    visitorsConverted,
    conversionRate: visitorsExposed > 0 ? Number(((visitorsConverted / visitorsExposed) * 100).toFixed(2)) : 0,
    timeSeries: row.timeSeries ?? []
  }
}

export const fetchOfferPerformance = async (offerIds: string[], start: string, end: string, timezone: string) => {
  const { currentTeamPid } = useConfigStore()
  const response = await api.post(`/api/${currentTeamPid}/metrics/offerStats`, {
    offerIds,
    start,
    end,
    timezone
  })
  return response.data
}
